import React, { useState, useMemo } from 'react';
import { Box, Typography, Stack, IconButton, Tooltip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';

const WORK_START = 9; // 09:00
const WORK_END = 18;  // 18:00
const HOURS = Array.from({ length: WORK_END - WORK_START }, (_, i) => WORK_START + i);
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

function getWeekDates(baseDate) {
  const d = new Date(baseDate);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
  const monday = new Date(d.setDate(diff));
  return Array.from({ length: 5 }, (_, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    return date;
  });
}

function formatDate(d) {
  return d.toISOString().slice(0, 10);
}

function formatShortDate(d) {
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

export default function ShortAbsenceCalendar({ absences = [], onSlotSelect }) {
  const theme = useTheme();
  const [weekOffset, setWeekOffset] = useState(0);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);

  const baseDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + weekOffset * 7);
    return d;
  }, [weekOffset]);

  const weekDates = useMemo(() => getWeekDates(baseDate), [baseDate]);

  const absenceMap = useMemo(() => {
    const map = {};
    absences.forEach(a => {
      const key = a.date;
      if (!map[key]) map[key] = [];
      map[key].push(a);
    });
    return map;
  }, [absences]);

  const handleMouseDown = (dayIndex, hour) => {
    setDragStart({ dayIndex, hour });
    setDragEnd({ dayIndex, hour });
  };

  const handleMouseEnter = (dayIndex, hour) => {
    if (dragStart && dayIndex === dragStart.dayIndex) {
      setDragEnd({ dayIndex, hour });
    }
  };

  const handleMouseUp = () => {
    if (dragStart && dragEnd && dragStart.dayIndex === dragEnd.dayIndex) {
      const startHour = Math.min(dragStart.hour, dragEnd.hour);
      const endHour = Math.max(dragStart.hour, dragEnd.hour) + 1;
      const date = weekDates[dragStart.dayIndex];
      onSlotSelect?.(
        formatDate(date),
        `${String(startHour).padStart(2, '0')}:00`,
        `${String(endHour).padStart(2, '0')}:00`
      );
    }
    setDragStart(null);
    setDragEnd(null);
  };

  const isInDragRange = (dayIndex, hour) => {
    if (!dragStart || !dragEnd || dayIndex !== dragStart.dayIndex) return false;
    const minH = Math.min(dragStart.hour, dragEnd.hour);
    const maxH = Math.max(dragStart.hour, dragEnd.hour);
    return hour >= minH && hour <= maxH;
  };

  const getAbsenceForSlot = (dayIndex, hour) => {
    const dateStr = formatDate(weekDates[dayIndex]);
    const dayAbsences = absenceMap[dateStr] || [];
    return dayAbsences.find(a => {
      const [sh] = a.startTime.split(':').map(Number);
      const [eh] = a.endTime.split(':').map(Number);
      return hour >= sh && hour < eh;
    });
  };

  const getSlotColor = (absence) => {
    if (!absence) return 'transparent';
    switch (absence.status) {
      case 0: case 'Pending': return alpha(theme.palette.warning.main, 0.4);
      case 1: case 'Approved': return alpha(theme.palette.success.main, 0.4);
      case 2: case 'Rejected': return alpha(theme.palette.error.main, 0.3);
      default: return 'transparent';
    }
  };

  const today = formatDate(new Date());

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={() => setWeekOffset(w => w - 1)}><ChevronLeftIcon /></IconButton>
        <IconButton size="small" onClick={() => setWeekOffset(0)}><TodayIcon /></IconButton>
        <IconButton size="small" onClick={() => setWeekOffset(w => w + 1)}><ChevronRightIcon /></IconButton>
        <Typography variant="body2" sx={{ ml: 1 }}>
          {formatShortDate(weekDates[0])} - {formatShortDate(weekDates[4])}
        </Typography>
      </Stack>

      <Box
        sx={{ display: 'grid', gridTemplateColumns: '50px repeat(5, 1fr)', userSelect: 'none' }}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { setDragStart(null); setDragEnd(null); }}
      >
        {/* Header */}
        <Box />
        {weekDates.map((d, i) => (
          <Box key={i} sx={{ textAlign: 'center', py: 0.5, fontWeight: formatDate(d) === today ? 'bold' : 'normal',
            bgcolor: formatDate(d) === today ? alpha(theme.palette.primary.main, 0.1) : 'transparent' }}>
            <Typography variant="caption">{WEEKDAYS[i]}</Typography>
            <Typography variant="body2">{d.getDate()}</Typography>
          </Box>
        ))}

        {/* Time slots */}
        {HOURS.map(hour => (
          <React.Fragment key={hour}>
            <Box sx={{ pr: 1, textAlign: 'right', fontSize: '0.75rem', color: 'text.secondary' }}>
              {`${hour}:00`}
            </Box>
            {weekDates.map((_, dayIndex) => {
              const absence = getAbsenceForSlot(dayIndex, hour);
              const inDrag = isInDragRange(dayIndex, hour);
              return (
                <Tooltip key={dayIndex} title={absence ? `${absence.startTime}-${absence.endTime}: ${absence.note || absence.statusText}` : 'Click and drag to select'}>
                  <Box
                    onMouseDown={() => !absence && handleMouseDown(dayIndex, hour)}
                    onMouseEnter={() => handleMouseEnter(dayIndex, hour)}
                    sx={{
                      height: 28, border: '1px solid', borderColor: 'divider', cursor: absence ? 'default' : 'crosshair',
                      bgcolor: absence ? getSlotColor(absence) : inDrag ? alpha(theme.palette.primary.main, 0.2) : 'background.paper',
                      '&:hover': { bgcolor: absence ? getSlotColor(absence) : alpha(theme.palette.primary.main, 0.1) }
                    }}
                  />
                </Tooltip>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

